import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { SnippetTabContext } from '@/components/IntegrationsHub'
import { ClosingCta } from '@/components/marketing/ClosingCta'
import { FeatureStory } from '@/components/marketing/FeatureStory'
import { Hero } from '@/components/marketing/Hero'
import { HowItWorks } from '@/components/marketing/HowItWorks'
import { Integrations } from '@/components/marketing/Integrations'
import { Pricing } from '@/components/marketing/Pricing'
import {
  FEATURE_NAV,
  SectionPillNav,
  USECASE_NAV,
} from '@/components/marketing/SectionPillNav'
import { UseCases, useCases } from '@/components/marketing/UseCases'
import { getPublicPlans } from '@/lib/billing/server'
import { getIntegrationSnippets } from '@/lib/marketing/server'
import { pageLinks, pageMeta, SITE_NAME, SITE_URL } from '@/lib/seo'

const DESCRIPTION =
  'Real US mobile carrier IPs over HTTP, HTTPS, or SOCKS5 for web scraping and AI data. Rotating or sticky sessions, city-level targeting, billed by the byte. From $30/mo.'

export const Route = createFileRoute('/')({
  loader: async () => {
    const [{ plans, trial }, { snippets }] = await Promise.all([
      getPublicPlans(),
      getIntegrationSnippets(),
    ])
    return { plans, trial, snippets }
  },
  head: () => ({
    meta: pageMeta({
      title: 'US Mobile Proxies on Real Carrier IPs · Masklabs',
      description: DESCRIPTION,
      path: '/',
    }),
    links: pageLinks('/'),
    // Organization + WebSite structured data — landing page only. No ratings
    // or address: we don't have real data to back those up. WebSite names the
    // site as an entity for search and AI-answer engines.
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: SITE_NAME,
          url: SITE_URL,
          description: DESCRIPTION,
        }),
      },
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: SITE_NAME,
          url: SITE_URL,
        }),
      },
      // Use cases as an ItemList — gives search and AI-answer engines a clean,
      // enumerated set of what Masklabs is used for, straight from the same data
      // the page renders so the two never drift.
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: 'Masklabs use cases',
          itemListElement: useCases.map((u, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: u.title,
            description: u.body,
          })),
        }),
      },
    ],
  }),
  component: Home,
})

function Home() {
  const { plans, trial, snippets } = Route.useLoaderData()
  // Shared by the hub figure (which sets it) and the integrations tabs.
  const [snippetTab, setSnippetTab] = useState('curl')
  return (
    <main className="flex w-full flex-col text-lg">
      <SnippetTabContext.Provider
        value={{ value: snippetTab, setValue: setSnippetTab }}
      >
        <Hero />
        <FeatureStory />
        <UseCases />
        <Integrations snippets={snippets} />
        <HowItWorks />
        <Pricing plans={plans} trial={trial} />
        <ClosingCta />
        <SectionPillNav
          sectionId="features"
          label="Features"
          items={FEATURE_NAV}
        />
        <SectionPillNav
          sectionId="use-cases"
          label="Use cases"
          items={USECASE_NAV}
        />
      </SnippetTabContext.Provider>
    </main>
  )
}
